import { Promise } from "core-js";
import JSZip from "jszip";
import isNumber from "lodash/isNumber";
import LevelManager from "../level-manager";
import MimeTypes from "./mime-types";
import PluginManager from "../plugin-manager";
import ResourceTypes from "./resource-types";

/**
 * Loading systems for projects in format gamefroot-4.0.0 and up.
 * This format contains information for both playing and editing a project.
 */
const LoaderGF4 = {
  /**
   * Parse and load necessary files.
   * 
   * @param {Object} gameJson - game data
   * @param {Phaser.Scene} scene - current game scene
   * @param {Loader} loader - base loader containing generic functionality
   */
  doCreate: function(gameJson, scene, loader) {

    // Parse data into a lite ResourceStore.
    const resourceStore = new ResourceStore(gameJson);

    // Assign the ResourceStore to the game, for universal access.
    scene.game.resourceStore = resourceStore;

    // Extract primary game.
    const game = resourceStore.getResourcesByType(ResourceTypes.Game)[0];
    if (!game) {
      scene.game.reportError("Game load error.\n\nThe game file was loaded, but did not include a game description.\n\nPlease contact Gamefroot directly and let us know about this problem!");
      return;
    }
    document.title = game.name;

    // Set up levels.
    const levels = game.levels.map(
      level => resourceStore.getExternalResource(game, level));
    levels.forEach((resource, index) => {
      // Check validity.
      if (!resource || resource.type !== ResourceTypes.Level) {
        return console.error("Level", index, "invalid:", resource);
      }
      LevelManager.add(resource, index + 1);
    });

    // Gather everything referenced by the game.
    const dependencies = new Map();
    resourceStore.getDependencies(game, dependencies);

    // Begin loading any font dependencies.
    const pretouches = [];
    const fontFaces = [];
    dependencies.forEach(dep => {
      if (dep.type === ResourceTypes.FontFace) {
        fontFaces.push(dep);
      }
    });
    const fontStyle = document.createElement("style");
    document.head.appendChild(fontStyle);
    const sheet = fontStyle.sheet;
    fontFaces.forEach(fontFace => {
      const fontFiles = fontFace.fontFiles.map(
        internalId => resourceStore.getExternalResource(fontFace, internalId))
        .filter(fontFile => {
          if (!fontFile) {
            console.error("Could not find fontFile resource", internalId, "on", fontFace);
            return false;
          }
          return true;
        });
      // Create a rule.
      const rule = `@font-face {
        font-family: ${fontFace.fontFamily};
        src: ${fontFiles.filter(fontFile => fontFile).map(fontFile =>
          `url("${fontFile.uri}") format("${fontFile.format}")`
        ).join(",\n  ")};
      }`;
      sheet.insertRule(rule, sheet.cssRules.length);

      // Pretouch the text. This is tucked in behind the game canvas.
      const pretouch = document.createElement("div");
      pretouch.style = `font-family: ${fontFace.fontFamily}; position: absolute; top: 0px; z-index: -1;`
      pretouch.innerText = `pretouch ${fontFace.fontFamily}`;
      document.body.appendChild(pretouch);
      pretouches.push(pretouch);
    });

    // Identify files for loading. Also inject script plugins.
    dependencies.forEach(resource => {
      const type = resource.type;
      if (type === ResourceTypes.Script) {
        PluginManager.registerScript(resource);
        return;
      }
      const { id, uri } = resource;

      // Phaser wants assets to be keyed by strings.
      const key = `${id}`;

      if (type === ResourceTypes.ImageFile) {
        if (uri.match(/^data:image/)) {
          scene.textures.addBase64(key, uri);
        } else {
          scene.load.image(key, uri);
          // We assume the URI is one of two forms:
          // uri.match(/^http[s]?:\/\//)
          // or a relative path for downloaded games, e.g. /assets\/images\/.*
        }
        return;
      }
      if (type === ResourceTypes.AudioFile) {
        scene.load.audio(key, uri);
        return;
      }
      if (type === ResourceTypes.ZipFile) {
        scene.load.binary(key, uri);
        return;
      }
    });

    // Update progress bar.
    // We only need a single stage for `gamefroot-4` files,
    // because they contain all necessary information.
    scene.load.on("progress", progress => {
      loader.updateProgress(scene, progress, 1, 0);
    });

    // Listen for files that fail to load.
    // If these are loaded from a URI that has redirections,
    // we may be able to jar them into working by using an alternate identifier.
    const failedFiles = [];
    scene.load.on("loaderror", file => {
      failedFiles.push(file);
    });

    // Begin loading.
    loader.loadAndRun(scene)
    .then(() => {
      // Try to redirect failed resources.
      if (failedFiles.length) {
        return new Promise((resolve, reject) => {
          failedFiles.forEach(file => {
            const uri = file.src
            .replace(
              "https://v3-files.gamefroot.com",
              "https://storage.googleapis.com/files.gamefroot.com"
            )
            .replace(
              "https://files.gamefroot.com",
              "https://storage.googleapis.com/users.gamefroot.com",
            );
            switch (file.type) {
              case "image":
                scene.load.image(file.key, uri);
                break;
              case "audio":
                scene.load.audio(file.key, uri);
                break;
              case "binary":
                scene.load.binary(file.key, uri);
                break;
            }
          });
          scene.load.start();
          scene.load.once("complete", () => {
            resolve();
          });
          scene.load.once("error", () => {
            scene.game.reportError("Couldn't load asset " + file.src);
            reject();
          });
        });
      }
    })
    .then(() => this.generateZipAssets(scene, dependencies, resourceStore))
    .then(() => {
      resourceStore.getResourcesByType(ResourceTypes.TileLayer).forEach(
        layer => this.generateTileset(layer, scene));

      // Loading complete. Prepare for level 1.
      scene.data.set("loadCompleted", true);
      if (scene.data.get("cameraFadedIn")) {
        loader.finishIndicators(scene);
      }
    })
    .catch(error => {
      console.error("Loader failed", error);
    });
  },

  /**
   * Extract the contents of ZIP files and make them addressable.
   */
  async generateZipAssets(scene, dependencies, resourceStore) {
    const game = scene.game;

    /** Symbolic key for storing zip data. */
    const _jsZip = Symbol("_jsZip");

    const audioToZip = [];
    const frameToZip = [];
    const zipPromises = [];
    dependencies.forEach(async resource => {
      switch (resource.type) {
        case ResourceTypes.ZipFile:
          // Attach decompressed ZIP data to ZipFile resources.
          const data = game.cache.binary.get(`${resource.id}`);
          const zip = new JSZip();
          resource[_jsZip] = zip;
          const promise = zip.loadAsync(data);
          zipPromises.push(promise);
          break;
        case ResourceTypes.Frame:
          // Find all Frames with FilePointers:
          // imageId: { id: number, key: string }
          // These use FileCollection data, currently ZipFileCollection data.
          const imageId = resource.imageId;
          if (!imageId) return;
          const keys = Object.keys(imageId);
          if (!(keys.includes("id") && keys.includes("key"))) return;
          frameToZip.push(resource);
          break;
        case ResourceTypes.AudioFilePointer:
          // Direct acquisition of audio from FileCollections.
          audioToZip.push(resource);
          break;
      }
    });
    await Promise.all(zipPromises).catch(err => console.error);

    // Prep for async load.
    const dataPromises = [];
    const loadPromises = [];

    // Load all desired textures into texture memory.
    const texturesUsed = new Set();
    frameToZip.forEach(frame => {
      // Get all the necessary resources.
      const zipFileCollection =
        resourceStore.getExternalResource(frame, frame.imageId.id);
      if (!zipFileCollection) return;
      const zipFile = resourceStore.getExternalResource(
        zipFileCollection, zipFileCollection.zipFile);
      if (!zipFile) return;
      const filePointer =
        zipFileCollection.files.find(file => file.key === frame.imageId.key);
      if (!filePointer) return;
      const imageFile =
        resourceStore.getExternalResource(zipFileCollection, filePointer.id);
      if (!imageFile) return;

      // Is this image already loading?
      const textureName = `${imageFile.id}`;
      if (
        game.textures.exists(textureName)
        || texturesUsed.has(textureName)
      ) {
        return;
      }
      texturesUsed.add(textureName);

      // Process the data.
      const zip = zipFile[_jsZip];
      const file = zip.file(frame.imageId.key);
      const data = file.async("arraybuffer");
      dataPromises.push(data);
      const loadPromise = new Promise((resolve, reject) => {
        data
        .then(data => {
          // Derive MIME type.
          let mimeType = MimeTypes.get("none");
          const extension = file.name.match(/\.(\w+)$/);
          if (extension) {
            const mime = MimeTypes.get(extension[1]);
            if (mime) mimeType = mime;
          }
          // Generate data.
          const blob = new Blob([data], { "type": mimeType });
          const url = URL.createObjectURL(blob);
          game.textures.addBase64(textureName, url);

          // Wait for the asynchronous texture load to complete.
          const listener = (key) => {
            if (key === textureName) {
              game.textures.off("onload", listener);
              resolve();
            }
          };
          game.textures.on("onload", listener);
          // TODO: Handle texture load error.
        })
        .catch(err => {
          // TODO: Handle extraction error
        });
      });
      loadPromises.push(loadPromise);

      // TODO: Fallbacks if the zip data fails.
      // We could load the ImageFile (hitherto excluded from loading),
      // or fall back to ImageFile proxy data.
    });

    // Load all desired audio data.
    let needsLoaderStart = false;
    audioToZip.forEach(audioFilePointer => {
      // Get all the necessary resources.
      const zipFileCollection =
        resourceStore.getExternalResource(
          audioFilePointer, audioFilePointer.fileCollection);
      if (!zipFileCollection) return;
      const zipFile = resourceStore.getExternalResource(
        zipFileCollection, zipFileCollection.zipFile);
      if (!zipFile) return;

      // Process the data.
      const zip = zipFile[_jsZip];
      const file = zip.file(audioFilePointer.key);
      const data = file.async("arraybuffer");
      dataPromises.push(data);
      data
      .then(data => {
        // Derive MIME type.
        let mimeType = MimeTypes.get("none");
        const extension = file.name.match(/\.(\w+)$/);
        if (extension) {
          const mime = MimeTypes.get(extension[1]);
          if (mime) mimeType = mime;
        }
        // Generate data.
        const blob = new Blob([data], { "type": mimeType });
        const url = URL.createObjectURL(blob);
        const resourceKey = `${audioFilePointer.id}`;
        // Load into Phaser.
        scene.load.audio(resourceKey, url);
        needsLoaderStart = true;
      })
      .catch(err => {
        // TODO: Handle extraction error
      });

      // TODO: Fallbacks if the zip data fails.
      // We could load the ImageFile (hitherto excluded from loading),
      // or fall back to ImageFile proxy data.
    });

    // Finish loading everything.
    await Promise.all(dataPromises);

    if (needsLoaderStart) {
      // Wait for audio files to load.
      scene.load.start();
      await new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!scene.load.isLoading()) {
            clearInterval(interval);
            resolve();
          }
        }, 1);
      });
    }

    return Promise.all(loadPromises);
  },

  /**
   * Generate a tileset texture for a layer. This combines all images
   * for tiles in that layer into a single spritesheet.
   * The spritesheet will be named `id + "-tile-spritesheet"`.
   * @param {Object} layer - TileLayer resource
   * @param {Phaser.Scene} scene - current scene
   */
  generateTileset(layer, scene) {
    const textureName = layer.id + "-tile-spritesheet";
    const dimension = Math.ceil(Math.sqrt(layer.brushes.length));

    // Add a 1-pixel margin around the tile frames.
    const fullTileWidth = layer.tileSize.x + 2;
    const fullTileHeight = layer.tileSize.y + 2;

    const canvasTexture = scene.textures.createCanvas(
      textureName,
      dimension * fullTileWidth,
      dimension * fullTileHeight);

    // Offsets for drawing margins. Corners, then edges, then center.
    const offsetCorners = [[0, 0], [0, 2], [2, 0], [2, 2]];
    const offsetEdges = [[0, 1], [1, 0], [2, 1], [1, 2]];
    const offsetCenter = [1, 1];

    const store = scene.game.resourceStore;

    /** Get the frame described by an {id, brush} descriptor. */
    const getIndexedFrame = (brushResource, brushIndex) => 
      store.getExternalResource(
        brushResource, brushResource.tiles[brushIndex]);

    layer.brushes.forEach((brush, index) => {
      /** Frame resource that describes a tile graphic. */
      let frameResource = typeof brush === "number"
      ? store.getExternalResource(layer, brush) // Direct link to a Frame.
      : !brush
      ? undefined // Falsy value that is not 0.
      : getIndexedFrame( // Assumed {id, index} description.
        store.getExternalResource(layer, brush.id),
        brush.index
      );
      if (!frameResource) {
        return;
      }

      const imageFileResource = store.getFrameImageFile(frameResource);
      if (!imageFileResource) {
        return;
      }
      const tex =
        scene.textures.getFrame(`${imageFileResource.id}`).source.image;
      const row = index % dimension;
      const col = Math.floor(index / dimension);
      const x = row * fullTileWidth;
      const y = col * fullTileHeight;

      /**
       * Draw the texture at the necessary dimensions,
       * clearing out the area it will occupy.
       */
      const applyTexture = (x, y) => {
        canvasTexture.context.clearRect(
          x, y, layer.tileSize.x, layer.tileSize.y);
        canvasTexture.context.drawImage(
          tex, x, y, layer.tileSize.x, layer.tileSize.y);
      };

      offsetCorners.forEach(offset => {
        applyTexture(x + offset[0], y + offset[1]);
      });
      offsetEdges.forEach(offset => {
        applyTexture(x + offset[0], y + offset[1]);
      });
      applyTexture(x + offsetCenter[0], y + offsetCenter[1]);
    });
    canvasTexture.update();
  }
};

/**
 * Interim representation of the data in the loaded game file.
 */
class ResourceStore {
  constructor(gameJson) {
    /** Collection of all elements within the game data. */
    this.resources = new Map();

    gameJson.resources.forEach(resource => {
      this.resources.set(resource.id, resource);
    });
  }

  /**
   * Return a list of all the resources with the listed IDs.
   * @param {Array<number>} ids - IDs to fetch
   */
  getByIds(ids) {
    return ids.map(id => this.resources.get(id));
  }

  /**
   * Return a list of all resources of a specific type.
   */
  getResourcesByType(type) {
    const output = [];
    this.resources.forEach(resource => {
      if (resource.type === type) {
        output.push(resource);
      }
    });
    return output;
  }

  /**
   * Return a Map of all dependencies that a given resource requires.
   * This will go recursive to follow all links.
   * 
   * ZipFileCollection will not fetch non-ZipFile dependencies,
   * because its dependencies are just record-keeping.
   * Similarly, AudioFilePointer will not fetch an AudioFile,
   * because it only depends on a FileCollection.
   * 
   * @param {Object} resource - resource to check
   * @param {Map<Object>} output - output map to be mutated
   */
  getDependencies(resource, output) {
    output.set(resource.id, resource);
    if (!resource.dependencies) {
      return;
    }
    let dependencies;
    if (resource.type === ResourceTypes.ZipFileCollection) {
      // Only get the ZipFile.
      dependencies = [this.getExternalResource(resource, resource.zipFile)];
    } else if (resource.type === ResourceTypes.AudioFilePointer) {
      // Only get the file collection.
      dependencies =
        [this.getExternalResource(resource, resource.fileCollection)];
    } else {
      // Standard resource: get all resources.
      dependencies = this.getByIds(
        resource.dependencies.map(dep => dep.externalId));
    }
    dependencies.forEach(dep => {
      if (!dep) {
        console.log("#DEBUG", "missing on", resource, dep);
        return;
      }
      if (output.has(dep.id)) {
        // Already processed.
        return;
      }
      this.getDependencies(dep, output);
    });
  }

  /**
   * Return a Resource defined by an ID internal to a base Resource.
   * Under `gamefroot-4`, an internal ID is mapped to an external ID
   * via a "dependency".
   * @param {Object} resource - resource making the request
   * @param {number} id - internal ID from the resource
   * @returns {Object | undefined} any matching resource
   */
  getExternalResource(resource, id) {
    const dependencies = resource.dependencies;
    if (!dependencies) {
      return console.error("Cannot fetch from non-dependency:", resource);
    }
    const dependency = dependencies.find(dep => dep.internalId === id);
    if (!dependency) {
      return;
    }
    return this.resources.get(dependency.externalId);
  }

  /**
   * Return the ImageFile resource associated with a Frame.
   * 
   * This is not as simple as getting an ImageFile dependency,
   * because Frames may depend on a FileCollection.
   * @param {Object} frame - Frame resource to analyse
   * @returns {Object | undefined} any matching ImageFile
   */
  getFrameImageFile(frame) {
    if (isNumber(frame.imageId)) {
      // Simple numeric ID.
      return this.getExternalResource(frame, frame.imageId);
    }
    const keys = Object.keys(frame.imageId);
    if (keys.includes("id") && keys.includes("key")) {
      // FilePointer data of form { id: number, key: string }
      const zipFileCollection =
        this.getExternalResource(frame, frame.imageId.id);
      if (!zipFileCollection) return;
      const file =
        zipFileCollection.files.find(file => file.key === frame.imageId.key);
      if (!file) return;
      return this.getExternalResource(zipFileCollection, file.id);
    }
  }
}

export default LoaderGF4;
